import { getRDFirstTableColumns,getRDSecondTableColumns } from "./StaticData";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import DragCommonTable from "./DragCommonTable";
import CommonModals from "./CommonModals";
import Popup from "./ModalWrapper";
import { postData,deleteData } from "../../../axios/apiHelper";
import { Button, Col } from "react-bootstrap";
import { ENDPOINT } from "../../../axios/apiConfig";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import Tooltip from "react-bootstrap/Tooltip";
import { OverlayTrigger } from "react-bootstrap";
import { loader } from "../../../loader";

const LinkWithTooltip = ({ id, children, href, tooltip }) => {
  return (
    <OverlayTrigger
      overlay={<Tooltip id={id}>{tooltip}</Tooltip>}
      placement="top"
      delayShow={300}
      delayHide={150}
    >
      <a href={href}>{children}</a>
    </OverlayTrigger>
  );
}

const PopHandler = ({ popArguments }) => {
  const {
    show,
    onClose,
    headerIcon,
    bodyText,
    headerText,
    headerClass,
    bodyClass,
    buttons,
  } = popArguments;

  return (
    <Popup
      show={show}
      onClose={onClose}
      headerIcon={headerIcon}
      headerClass={headerClass}
      headerText={headerText}
    >
      <CommonModals
        bodyText={bodyText}
        bodyClass={bodyClass}
        Buttons={buttons}
      />
    </Popup>
  );
};


const RDTopics = () => {
  const path_image = "https://informed.pro/componentAssets/images/"
  const [editingRowId, setEditingRowId] = useState(null);
  const [offlineData, setOfflineData] = useState([]);
  const [groupTopics, setGroupTopics] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const draggedRowRef = useRef(null);
  const dragSourceRef = useRef(null);
  const editingInputRef = useRef(null);
  const [errorFlag, setErrorFlag] = useState('');
  const [apiCallFlag, setApiCallFlag] = useState(true);
  const [popArguments, setPopArguments] = useState({
    show: false,
    headerText: null,
    onClose: () => { },
    headerIcon: "alert.png",
    bodyText: " ",
    buttons: [],
    headerClass: "",
    bodyClass: "",
  });

  useEffect(() => {
    if (editingInputRef.current) {
      editingInputRef.current.focus();
    }
  }, [editingRowId]);

  useEffect(() => {
    getTopicsData()
  }, []);

  const handleClose = useCallback(() => {
    setPopArguments((prev) => ({ ...prev, show: false }));
  },[]);

  const handlePopArguments = useCallback(
    (show = true, handleClose, headerIcon, headerText, headerClass, bodyClass, bodyText, buttons) => {
      setPopArguments({
        show,
        onClose: () => handleClose(),
        headerIcon,
        headerText,
        headerClass,
        bodyClass,
        bodyText,
        buttons,
      });
    },
    []
  );

  const handleEdit = (_value, row) => {
    if(editingRowId && editingRowId !== row.id){
      if (editingInputRef.current) {
        toast.error("Please save the previous one.")
        setErrorFlag(true)
        editingInputRef.current.focus();
      }
      return;
    }else{
      setErrorFlag(false)
    }
    setEditingRowId(row.id);
  };

  const handleSaveEditedRow = async (row, type, parentId = 0) => {
    const isNewRow = typeof row.id === "string" && row.id.startsWith("temp-");

    if(row.product.trim() == ''){
      toast.error("Topics should not be empty")
      return
    }

    if (isNewRow) {
      await handleSaveNewRow(row, type, parentId);
      return;
    }

    const payload = {
      product: row.product,
      color: row.product_color,
      type: type === "parent" ? 20 : 2,
    };

    const updateFn = row.live_status === 1 ? setGroupTopics : setOfflineData;

    updateFn((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, ...payload } : item
      )
    );
    setEditingRowId(null);

    try {
      await postData(`${ENDPOINT.UPDATE_TOPIC}${row.id}`, payload);
    } catch (err) {
      setEditingRowId(row.id);
      console.error("Edit failed:", err);
    }
  };

  const handleSaveNewRow = useCallback(async (row, type, parentId = 0) => {
    const tempId = row.id;
    let topicColor = row.product_color;
    if(row.product.trim().length == 0){
      toast.error("Topics should not be empty")
      return;
    }
    const optimisticRow = { ...row, type: "existing" };
    if (type === "parent") {
      setGroupTopics((prev) => {
        return prev.map((item) =>
          item.id === tempId ? optimisticRow : item
        );
      });
    } else if (type === "child" && parentId !== 0) {
      setGroupTopics((prev) =>
        prev.map((group) => {
          if (group.id === parentId) {
            topicColor = group.product_color
            const topics = group.topics || [];
            const existingIndex = topics.findIndex((topic) => topic.id === tempId);
            const newTopics =
              existingIndex !== -1
                ? topics.map((t, i) => (i === existingIndex ? optimisticRow : t))
                : [...topics, optimisticRow];
            return { ...group, topics: newTopics };
          }
          return group;
        })
      );
    } else {
      setOfflineData((prev) =>
        prev.map((item) => (item.id === tempId ? optimisticRow : item))
      );
    }

    setEditingRowId(null);
    
    // Send the request to backend
    try {
      const payload = {
        user_id: localStorage.getItem("user_id"),
        product: row.product,
        category: type === "parent" ? 0 : 4,
        type: type === "parent" ? 20 : 2,
        color: topicColor,
        sort_order: row?.sort_order,
        parent_id: parentId,
      };

      const res = await postData(ENDPOINT.ADD_SPC_PRODUCT, payload);
      const newId = res?.data?.data?.id;
      toast.success("New Topic added")
      if (!newId) throw new Error("No ID returned from API");

      // Replace temp ID with real one
      if (type === "parent") {
        setGroupTopics((prev) =>
          prev.map((item) =>
            item.id === tempId ? { ...item, id: newId } : item
          )
        );
      } else if (type === "child" && parentId !== 0) {
        setGroupTopics((prev) =>
          prev.map((group) => {
            if (group.id === parentId) {
              const topics = group.topics || [];
              const updatedTopics = topics.map((t) =>
                t.id === tempId ? { ...t, id: newId } : t
              );
              return { ...group, topics: updatedTopics };
            }
            return group;
          })
        );
      } else {
        setOfflineData((prev) =>
          prev.map((item) => (item.id === tempId ? { ...item, id: newId } : item))
        );
      }
    } catch (err) {
      setEditingRowId(tempId);
      console.error("Save failed:", err);
      // Optionally roll back or show error state
    }
  },[]);

  const handleChange = useCallback((id, field, value, type, parentId = 0) => {
    if (type == "parent") {
      setGroupTopics((prev) => {
        return prev.map((item) =>
          item.id === id ? { ...item, [field]: value } : item
        );
      });
    } else if (type == 'child' && parentId != 0) {
      setGroupTopics((prev) => prev.map((group) =>
        group.id === parentId
          ? { ...group, topics: group.topics?.map((topic) => topic.id === id ? { ...topic, [field]: value } : topic) }
          : group
      ));
    } else {
      setOfflineData((prev) => {
        return prev.map((item) =>
          item.id === id ? { ...item, [field]: value } : item
        );
      });
    }
  }, []);

  const handleAddRow = (clickedRowIndex, type, parentId = 0) => {
    if(editingRowId){
      if (editingInputRef.current) {
        toast.error("Please save the previous one.")
        setErrorFlag(true)
        editingInputRef.current.focus();
      }
      return;
    }else{
      setErrorFlag(false)
    }

    let color = '';
    if (type === "child" && parentId !== 0) {
      const parentGroup = groupTopics.find(group => group.id === parentId);
      color = parentGroup ? parentGroup.product_color : '';
    }

    const newRow = {
      id: `temp-${Date.now()}`,
      parent_id: parentId,
      product: "",
      type: "new",
      category: type === "parent" ? 0 : 4,
      mandatory: 0,
      product_color: color,
      sort_order: 0,
      is_deleted: 0,
    };

    // Helper to update sort order
    const insertAndResort = (list, index, item) => {
      const newList = [...list];
      newList.splice(index + 1, 0, item);
      return newList.map((row, i) => ({ ...row, sort_order: i + 1 }));
    };

    if (type === "parent") {
      const updated = insertAndResort(groupTopics, clickedRowIndex, newRow);
      setGroupTopics(updated);
    } else if (type === "child" && parentId !== 0) {
      setGroupTopics((prev) =>
        prev.map((group) => {
          if (group.id !== parentId) return group;

          const childList = group.topics || [];
          const updatedTopics = insertAndResort(childList, clickedRowIndex, newRow);

          return { ...group, topics: updatedTopics };
        })
      );
    } else if (type === "child") {
      const updated = insertAndResort(offlineData, clickedRowIndex, newRow);
      setOfflineData(updated);
    }
    setEditingRowId(newRow.id);
  };

  const handleTopicDeleteConfirmation = async (row, type, parentId = 0) => {
    const topicId = row?.id;
    const isTemp = typeof topicId === "string" && topicId.startsWith("temp-");
  
    // Optimistically update the UI first
    if (topicId === editingRowId) {
      setEditingRowId(null);
    }
  
    if (type === 'parent') {
      setGroupTopics(prev => {
        return prev.reduce((acc, group) => {
          if (group.id === topicId) {
            if (group.topics?.length) {
              setOfflineData(offlinePrev => [...offlinePrev, ...group.topics]);
            }
            return acc; // skip this group (i.e., delete it)
          }
          acc.push(group);
          return acc;
        }, []);
      });
    } else if (type === 'child' && parentId !== 0) {
      setGroupTopics(prev =>
        prev.map(group =>
          group.id === parentId
            ? {
                ...group,
                topics: group.topics?.filter(topic => topic.id !== topicId),
              }
            : group
        )
      );
    } else {
      setOfflineData(prev => prev.filter(item => item.id !== topicId));
    }
    handleClose();
    try {
      if (!isTemp) {
        deleteData(ENDPOINT.SPC_PRO_DELETE,topicId);
      }
  
      handlePopArguments(
        true,
        handleClose,
        "success.png",
        "",
        "",
        "",
        <div
          dangerouslySetInnerHTML={{
            __html: "The topic has been deleted<br/>successfully!",
          }}
        />,
        [
          {
            text: "Close",
            click: () => handleClose(),
            className: "btn-white",
          },
        ]
      );
    } catch (error) {
      console.error("Delete failed:", error);
      // Optional: show error toast or rollback state
    }
  };
  

  const handleDelete = (_value, row, type, parentId = 0) => {
    handlePopArguments(
      true,
      handleClose,
      "alert.png",
      "",
      "",
      "",
      <div
        dangerouslySetInnerHTML={{
          __html:
            "You are about to remove this topic forever.<br/>Are you sure you want to do this?",
        }}
      />,
      [
        {
          text: "Yes Please!",
          click: () => handleTopicDeleteConfirmation(row, type, parentId),
          className: "btn btn-primary btn-filled",
        },
        {
          text: "Cancel",
          click: () => handleClose(),
          className: "btn btn-primary btn-bordered",
        },
      ]
    );
  };

  

  const getTopicsData = useCallback(async () => {
    try {
      loader("show");
      const [topics, groupTopics] = await Promise.all([
        postData(ENDPOINT.SPC_PRO_LISTING, {
          user_id: localStorage.getItem("user_id"),
          type: 2,
          category: 4,
          isRdLib: 1
        }),
        postData(ENDPOINT.GETGROUPTOPICS),
      ]);
    console.log(topics,groupTopics)
      // For offlineData (topics)
      const offlineTopics = (topics?.data?.data?.data ?? [])
        .map((item, idx) => ({
          ...item,
          sort_order: !item.sort_order || item.sort_order === "" ? idx + 1 : item.sort_order,
          type: "existing",
        }))
        .sort((a, b) => Number(a.sort_order) - Number(b.sort_order));
      setOfflineData(offlineTopics);

      // For groupTopics
      const groupTopicsData = (groupTopics?.data?.data ?? []).map((item, idx) => ({
        ...item,
        sort_order: !item.sort_order || item.sort_order === "" ? idx + 1 : item.sort_order,
        type: "existing",
      }));
      setGroupTopics(groupTopicsData);

      setApiCallFlag(false)
    } catch (error) {
      console.error("Failed to fetch topics data:", error);
    } finally{
      loader("hide");
    }
  },[]);

  const updateChildTopics = useCallback((parentId, updaterFn) => {
    setGroupTopics((prev) =>
      prev.map((group) =>
        group.id === parentId
          ? { ...group, topics: updaterFn(group.topics ?? []) }
          : group
      )
    );
  },[]);

  const handleInternalDrop = (tableType, index, parentId = null) => {
    const draggedRow = draggedRowRef.current;
    if (!draggedRow) return;

    if (tableType === "offline") {
      setOfflineData((prev) => reorderAndSync(prev, draggedRow, index, 2));
    } else if (tableType === "child-topics" && parentId != null) {
      updateChildTopics(parentId, (prevTopics) => reorderAndSync(prevTopics, draggedRow, index, 2, parentId));
    } else if (tableType === "group-topics") {
      setGroupTopics((prev) => reorderAndSync(prev, draggedRow, index, 20));
    }
  };

  const reorderAndSync = useCallback((items, draggedRow, index, type, parentId = 0) => {
    const filtered = items.filter((item) => item.id !== draggedRow.id);
    filtered.splice(index, 0, draggedRow);
    const updated = filtered.map((item, idx) => ({
      ...item,
      sort_order: idx + 1,
    }));
    const sortData = updated
    .filter(item => typeof item.id === "number" || !item.id.toString().startsWith("temp-"))
    .map(({ id, sort_order }) => ({ id, sort_order }));
    updateTopicSortOrder(sortData, type, parentId);
    return updated;
  },[]);

  const updateTopicSortOrder = useMemo(
    () => debounce((sortData, type, parentId) => {
      (async () => {
        try {
          await postData(`${ENDPOINT.SORTTOPICS}`, { sortData, type, parentId });
        } catch (err) {
          console.error("Failed to update topic sort order", err);
        }
      })();
    }, 200),
    []
  );

  const handleExternalDropToChildTopics = async (index, draggedRow, draggedSource, parentId) => {
    if (draggedSource === "offline") {
      setOfflineData((prev) =>
        prev.filter((item) => item.id !== draggedRow.id)
      );

      let color = '';
      if (parentId !== 0) {
        const parentGroup = groupTopics.find(group => group.id === parentId);
        color = parentGroup ? parentGroup.product_color : '';
      }


      let updatedTopics = [];
      updateChildTopics(parentId, (topics) => {
        const newTopics = [...topics];
        newTopics.splice(index, 0, draggedRow);

        updatedTopics = newTopics.map((item, idx) => ({
          ...item,
          sort_order: idx + 1,
          product_color: color
        }));

        return updatedTopics;
      });

      
      try {
        await postData(ENDPOINT.UPDATE_TOPIC_PARENT, {
          topic_id: draggedRow.id,
          new_parent_id: parentId,
          sort_order: index + 1,
          color:color
        });

        await postData(ENDPOINT.SORTTOPICS, {
          sortData: updatedTopics.map(({ id, sort_order }) => ({
            id,
            sort_order,
          })),
          type: 2,
          parentId: parentId
        });
      } catch (err) {
        console.error("Failed to move topic from offline to group", err);
      }
    }
  };

  return (
    <>
      <Col className="right-sidebar custom-change onesource-opition">
        <div className="custom-container">
          <div className="top-header sticky">
            <div className="page-title">
              <h2>Topics</h2>
            </div>
          </div>
          <div className="rd-topics">
              <h4>The order of the groups and topics will be reflected in the Docintel app filter</h4>

              <h5>
                Grouped Topics <LinkWithTooltip tooltip={"Multiple topics organized under one category. Deleting the group moves its topics to the Topics section below."}>
                  <img src={path_image + "info_circle_icon.svg"} alt="tooltip-btn" />
                </LinkWithTooltip>
              </h5>
            <div className="onesource-table rd-table rd-first">
              {
                groupTopics?.length > 0
                  ?
                  <DragCommonTable
                    className="highlight-visible"
                    data={groupTopics}
                    setData={setGroupTopics}
                    columns={getRDFirstTableColumns(
                      () => { },
                      handleDelete,
                      { showTitle: true },
                      handleEdit,
                      handleAddRow,
                      editingRowId,
                      handleChange,
                      handleSaveEditedRow,
                      setExpandedRowId,
                      expandedRowId,
                      editingInputRef,
                      errorFlag
                    )}
                    editingRowId={editingRowId}
                    draggable={!expandedRowId ? true : false}
                    expandedRowId={expandedRowId}
                    draggedRowRef={draggedRowRef}
                    dragSourceRef={dragSourceRef}
                    type="group-topics"
                    sourceType="group-topics"
                    onInternalDrop={(index) => handleInternalDrop("group-topics", index)}
                    renderExpandedContent={(row) => (
                      <DragCommonTable
                        className="highlight-visible"
                        data={row.topics || []}
                        setData={(newTopics) => updateChildTopics(row.id, () => newTopics)}
                        columns={getRDSecondTableColumns(
                          () => { },
                          handleDelete,
                          { showTitle: true },
                          handleEdit,
                          handleAddRow,
                          editingRowId,
                          handleChange,
                          handleSaveEditedRow,
                          "child-topics",
                          errorFlag,
                          editingInputRef,
                          row.id
                        )}
                        editingRowId={editingRowId}
                        draggable
                        draggedRowRef={draggedRowRef}
                        dragSourceRef={dragSourceRef}
                        sourceType="child-topics"
                        type="child-topics"
                        onInternalDrop={(index) =>
                          handleInternalDrop("child-topics", index, row.id)
                        }
                        onExternalDrop={(index, draggedRow, draggedSource) =>
                          handleExternalDropToChildTopics(index, draggedRow, draggedSource, row.id)
                        }
                        handleAddRow={handleAddRow}
                        parentId={row.id}
                      />
                    )}
                  />
                  :
                  !apiCallFlag ?
                  <div className="data-not-available d-flex justify-content-end align-items-center">
                    <p>No New Group Topic</p>
                    <LinkWithTooltip tooltip="Add new topic">
                      <Button className="add" onClick={e => handleAddRow(0, "parent")}>
                        <img src={path_image + "add-button-blue.svg"} alt=""/>
                      </Button>
                    </LinkWithTooltip>
                  </div>
                  : null
              }
            </div>
            <h5>Topics <LinkWithTooltip tooltip={"Individual topics that are not assigned to any group or category."}>
                  <img src={path_image + "info_circle_icon.svg"} alt="tooltip-btn" />
                </LinkWithTooltip></h5>
            <div className="onesource-table rd-table">
              {offlineData.length > 0 ?
              (
                <DragCommonTable
                  className="highlight-visible"
                  data={offlineData}
                  setData={setOfflineData}
                  columns={getRDSecondTableColumns(
                    () => { },
                    handleDelete,
                    { showTitle: true },
                    handleEdit,
                    handleAddRow,
                    editingRowId,
                    handleChange,
                    handleSaveEditedRow,
                    "topics",
                    errorFlag,
                    editingInputRef
                  )}
                  editingRowId={editingRowId}
                  draggable={true}
                  draggedRowRef={draggedRowRef}
                  dragSourceRef={dragSourceRef}
                  onInternalDrop={(index) => handleInternalDrop("offline", index)}
                  type="topics"
                  sourceType="offline"
                />
              ) :
                !apiCallFlag ?
                <div className="data-not-available d-flex justify-content-end align-items-center">
                  <p>No New Topic</p>
                  <LinkWithTooltip tooltip="Add new topic">
                    <Button className="add" onClick={e => handleAddRow(0, "child")}>
                      <img src={path_image + "add-button-blue.svg"} alt="" />
                    </Button>
                  </LinkWithTooltip>
                </div>
                : null
              }
              <PopHandler popArguments={popArguments} />
            </div>
          </div>
        </div>
      </Col>
    </>
  );
};

export default RDTopics;